import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  ArrowRightMdIcon,
  ArrowLeftMdIcon,
  ArrowUpSmIcon,
  ArrowDownSmIcon,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { CronoSearchBar } from 'components/CronoSearchBar';
import { fullName } from 'crono-fe-common/utils';
import {
  CSSProperties,
  RefCallback,
  RefObject,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { cn } from 'utils/style-utils';
import { usePickerPlacement } from './usePickerPlacement';
import { findTeamColor } from './utils';

interface ClassNameBySize {
  popoverContent: string;
  listItem: string;
  trigger?: string;
  rightPadding?: string;
}

const classNameBySize: Record<'sm' | 'md', ClassNameBySize> = {
  sm: {
    popoverContent: 'w-[var(--radix-popover-trigger-width)] p-1',
    listItem: 'min-h-8 rounded-md',
    trigger: 'text-sm h-8',
    rightPadding: 'pr-1',
  },
  md: {
    popoverContent: 'w-[300px] p-2',
    listItem: 'min-h-10 rounded-lg',
    trigger: 'min-h-10',
    rightPadding: 'pr-2',
  },
};

interface ListItemProps {
  label: string;
  isSelected?: boolean | 'indeterminate';
  multiple?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  className?: string;
  onToggle?: () => void;
  onClick?: () => void;
  size?: 'sm' | 'md';
}

const ListItem = ({
  label,
  isSelected,
  multiple,
  prefix,
  suffix,
  className,
  onToggle,
  onClick,
  size = 'md',
}: ListItemProps) => {
  const classNames = classNameBySize[size];

  return (
    <div
      className={cn(
        'flex items-center px-2 py-1 cursor-pointer transition-colors hover:bg-gray-100 text-xs font-medium',
        classNames.listItem,
        { 'bg-gray-100': isSelected && !multiple },
        className,
      )}
      onClick={onClick}
    >
      {multiple && (
        <Checkbox
          className="mr-3"
          checked={isSelected}
          aria-label={label}
          onCheckedChange={onToggle}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      )}
      {prefix}
      <Tooltip>
        <TooltipTrigger asChild>
          <p className="truncate flex-1 max-w-fit">{label}</p>
        </TooltipTrigger>
        <TooltipContent className="z-99999">{label}</TooltipContent>
      </Tooltip>
      {suffix}
    </div>
  );
};

export interface TeamsUsersSelectViewTeam {
  id: number;
  name: string;
}

export interface TeamsUsersSelectViewUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

interface TeamsUsersSelectHeaderProps {
  size: 'sm' | 'md';
  teamsList: TeamsUsersSelectViewTeam[];
  selectedTeam: TeamsUsersSelectViewTeam | undefined;
  allUsersSelected: boolean;
  onClickHeader: () => void;
}

const TeamsUsersSelectHeader = ({
  size,
  teamsList,
  selectedTeam,
  allUsersSelected,
  onClickHeader,
}: TeamsUsersSelectHeaderProps) => {
  if (selectedTeam) {
    return (
      <ListItem
        size={size}
        label={selectedTeam.name}
        className="overflow-hidden"
        prefix={
          <>
            <ArrowLeftMdIcon className="size-6 text-gray-700 mr-3" />
            <div
              className="size-1.5 shrink-0 rounded-full mr-2"
              style={{
                background: findTeamColor(
                  teamsList.findIndex((t) => t.id === selectedTeam.id),
                ),
              }}
            />
          </>
        }
        onClick={onClickHeader}
      />
    );
  }

  if (allUsersSelected) {
    if (teamsList.length === 0) return null;
    return (
      <ListItem
        size={size}
        label="All users"
        prefix={<ArrowLeftMdIcon className="size-6 text-gray-700 mr-3" />}
        onClick={onClickHeader}
      />
    );
  }

  return (
    <ListItem
      size={size}
      label="All users"
      suffix={<ArrowRightMdIcon className="size-6 text-gray-700 ml-auto" />}
      onClick={onClickHeader}
    />
  );
};

interface TeamsUsersSelectListProps {
  showUsersList: boolean;
  teamsList: TeamsUsersSelectViewTeam[];
  size: 'sm' | 'md';
  multiple: boolean;
  onClickTeam: (teamId: number) => void;
  isLoadingUsers: boolean;
  users: TeamsUsersSelectViewUser[];
  usersSearch: string;
  selectedUsersIds: number[] | number | undefined;
  currentUserId?: number;
  onToggleUser: ((userId: number) => void) | undefined;
  onClickUser: ((userId: number) => void) | undefined;
}

const TeamsUsersSelectList = ({
  showUsersList,
  teamsList,
  size,
  multiple,
  onClickTeam,
  isLoadingUsers,
  users,
  usersSearch,
  selectedUsersIds,
  currentUserId,
  onToggleUser,
  onClickUser,
}: TeamsUsersSelectListProps) => {
  const classNames = classNameBySize[size];

  if (!showUsersList) {
    return (
      <>
        {teamsList.map((t, index) => (
          <ListItem
            size={size}
            key={t.id}
            label={t.name}
            prefix={
              <div
                className="size-1.5 shrink-0 rounded-full mr-2"
                style={{ background: findTeamColor(index) }}
              />
            }
            suffix={
              <ArrowRightMdIcon className="size-6 text-gray-700 shrink-0 ml-auto" />
            }
            onClick={() => onClickTeam(t.id)}
          />
        ))}
      </>
    );
  }

  if (isLoadingUsers) {
    return (
      <>
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={cn('flex items-center gap-3 px-2', classNames.listItem)}
          >
            {multiple && (
              <div className="bg-neutral-300 animate-pulse h-4 w-4 rounded-sm shrink-0" />
            )}
            <div className="bg-neutral-300 animate-pulse h-3 w-32 rounded-sm" />
          </div>
        ))}
      </>
    );
  }

  if (users.length === 0) {
    return (
      <p className="px-2 min-h-14 text-xs font-medium text-gray-700 flex justify-center items-center">
        {usersSearch ? 'No results found' : 'No users found'}
      </p>
    );
  }

  return (
    <>
      {users.map((u) => {
        const isSelected = multiple
          ? Array.isArray(selectedUsersIds) && selectedUsersIds.includes(u.id)
          : selectedUsersIds === u.id;
        return (
          <ListItem
            size={size}
            key={u.id}
            label={fullName(u.firstName, u.lastName)}
            suffix={
              u.id === currentUserId && (
                <span className="ml-1 shrink-0 text-gray-700">(me)</span>
              )
            }
            isSelected={isSelected}
            multiple={multiple}
            onToggle={() => onToggleUser?.(u.id)}
            onClick={
              multiple ? () => onToggleUser?.(u.id) : () => onClickUser?.(u.id)
            }
          />
        );
      })}
    </>
  );
};

interface TeamsUsersSelectViewBaseProps {
  size?: 'sm' | 'md';
  triggerClassName?: string;
  /** Shown before the label on the default trigger. */
  triggerIcon?: React.ReactNode;
  popoverClassName?: string;
  children?: React.ReactNode;
  collisionBoundary?: Element | null;
  side?: 'top' | 'bottom' | 'left' | 'right';
  forceTriggerWidth?: boolean;
  /** Shows the selection but can't be opened. */
  disabled?: boolean;
  popoverOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Final label to render on the trigger — the selection label, or the
   * placeholder when nothing is selected. */
  triggerLabel: string | undefined;
  /** Whether `triggerLabel` reflects a real selection (vs. the placeholder),
   * used only to pick the placeholder text color. */
  hasSelection: boolean;
  teamsList: TeamsUsersSelectViewTeam[];
  selectedTeamId: number | undefined;
  allUsersSelected: boolean;
  isLoadingUsers: boolean;
  users: TeamsUsersSelectViewUser[];
  usersSearch: string;
  currentUserId?: number;
  onSearchChange: (text: string) => void;
  usersScrollRef: RefObject<HTMLDivElement>;
  sentinelRef: RefCallback<HTMLDivElement>;
  onClickTeam: (teamId: number) => void;
  onClickHeader: () => void;
}

interface TeamsUsersSelectViewMultipleProps extends TeamsUsersSelectViewBaseProps {
  multiple: true;
  selectedUsersIds: number[];
  onToggleUser: (userId: number) => void;
  onToggleAllTeamUsers: (checked: boolean) => void;
}

interface TeamsUsersSelectViewSingleProps extends TeamsUsersSelectViewBaseProps {
  multiple?: false;
  selectedUsersIds: number | undefined;
  onClickUser: (userId: number) => void;
}

export type TeamsUsersSelectViewProps =
  | TeamsUsersSelectViewMultipleProps
  | TeamsUsersSelectViewSingleProps;

export const TeamsUsersSelectView = (props: TeamsUsersSelectViewProps) => {
  const {
    size = 'md',
    triggerClassName,
    triggerIcon,
    popoverClassName,
    children,
    collisionBoundary,
    side,
    forceTriggerWidth = false,
    disabled = false,
    popoverOpen: requestedOpen,
    onOpenChange,
    triggerLabel,
    hasSelection,
    teamsList,
    selectedTeamId,
    allUsersSelected,
    isLoadingUsers,
    users,
    usersSearch,
    currentUserId,
    onSearchChange,
    usersScrollRef,
    sentinelRef,
    onClickTeam,
    onClickHeader,
    multiple,
    selectedUsersIds,
  } = props;

  // A disabled picker stays closed even when its host asks for it open.
  const popoverOpen = requestedOpen && !disabled;
  const classNames = classNameBySize[size];
  const showUsersList = !!selectedTeamId || allUsersSelected;
  const selectedTeam = teamsList.find((t) => t.id === selectedTeamId);
  const showSearch =
    showUsersList && (isLoadingUsers || users.length > 0 || !!usersSearch);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const {
    side: resolvedSide,
    availableHeight,
    resolvePlacement,
  } = usePickerPlacement({
    triggerRef,
    open: popoverOpen,
    boundary: collisionBoundary,
    forcedSide: side,
  });

  function handleOpenChange(open: boolean) {
    if (open && disabled) return;
    if (open) resolvePlacement();
    onOpenChange(open);
  }

  useEffect(() => {
    if (!popoverOpen || !showSearch) return;
    searchInputRef.current?.focus();
  }, [popoverOpen, showSearch]);

  const allTeamUsersSelected = useMemo(() => {
    if (!multiple || users.length === 0) return false;
    return users.every((u) => props.selectedUsersIds.includes(u.id));
  }, [multiple, users, multiple ? props.selectedUsersIds : null]);

  const someTeamUsersSelected = useMemo(() => {
    if (!multiple || allTeamUsersSelected) return false;
    return users.some((u) => props.selectedUsersIds.includes(u.id));
  }, [
    multiple,
    users,
    allTeamUsersSelected,
    multiple ? props.selectedUsersIds : null,
  ]);

  const popoverStyle = availableHeight
    ? ({ '--picker-available-h': `${availableHeight}px` } as CSSProperties)
    : undefined;

  return (
    <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild ref={triggerRef}>
        {children ? (
          children
        ) : (
          <div
            aria-disabled={disabled || undefined}
            className={cn(
              'flex items-center border border-gray-300 rounded-lg px-2 flex-1 justify-between overflow-hidden',
              disabled
                ? 'cursor-not-allowed bg-neutral-100 text-gray-700'
                : 'cursor-pointer',
              classNames.trigger,
              { 'text-gray-700': !hasSelection },
              triggerClassName,
            )}
          >
            {triggerIcon}
            <span className="truncate flex-1">{triggerLabel}</span>

            {popoverOpen ? (
              <ArrowUpSmIcon className="size-4 text-gray-700" />
            ) : (
              <ArrowDownSmIcon className="size-4 text-gray-700" />
            )}
          </div>
        )}
      </PopoverTrigger>

      <PopoverContent
        className={cn(
          'max-h-[min(var(--picker-max-h,452px),var(--picker-available-h,100dvh),var(--radix-popover-content-available-height,100dvh),calc(100dvh-1rem))] pr-0! flex flex-col overflow-hidden z-99999 min-w-40',
          classNames.popoverContent,
          {
            'w-(--radix-popover-trigger-width)!': forceTriggerWidth,
          },
          popoverClassName,
        )}
        style={popoverStyle}
        collisionBoundary={collisionBoundary ?? undefined}
        collisionPadding={8}
        side={resolvedSide}
        hideWhenDetached
        onOpenAutoFocus={(event) => {
          if (!searchInputRef.current) return;
          event.preventDefault();
          searchInputRef.current.focus();
        }}
      >
        <div className="flex flex-col overflow-hidden flex-1">
          <div className={cn(classNames.rightPadding)}>
            <TeamsUsersSelectHeader
              size={size}
              teamsList={teamsList}
              selectedTeam={selectedTeam}
              allUsersSelected={allUsersSelected}
              onClickHeader={onClickHeader}
            />

            {showUsersList && teamsList.length > 0 && (
              <div className="h-px w-full bg-neutral-300" />
            )}
            {showSearch && (
              <div className="px-2 py-1">
                <CronoSearchBar
                  setDebouncedSearch={onSearchChange}
                  initialSearch={usersSearch}
                  placeholder="Search users..."
                  id="usersSearch"
                  className="border-b-0!"
                  size={size}
                  inputRef={searchInputRef}
                />
              </div>
            )}

            {multiple && showUsersList && users.length > 0 && (
              <ListItem
                size={size}
                label={allTeamUsersSelected ? 'Deselect all' : 'Select all'}
                isSelected={
                  someTeamUsersSelected ? 'indeterminate' : allTeamUsersSelected
                }
                multiple
                className="text-gray-700"
                onToggle={() =>
                  props.onToggleAllTeamUsers(!allTeamUsersSelected)
                }
                onClick={() =>
                  props.onToggleAllTeamUsers(!allTeamUsersSelected)
                }
              />
            )}
          </div>

          <div
            ref={usersScrollRef}
            className={cn(
              'flex flex-col overflow-auto flex-1',
              classNames.rightPadding,
            )}
          >
            <TeamsUsersSelectList
              showUsersList={showUsersList}
              teamsList={teamsList}
              size={size}
              multiple={!!multiple}
              onClickTeam={onClickTeam}
              isLoadingUsers={isLoadingUsers}
              users={users}
              usersSearch={usersSearch}
              selectedUsersIds={selectedUsersIds}
              currentUserId={currentUserId}
              onToggleUser={multiple ? props.onToggleUser : undefined}
              onClickUser={!multiple ? props.onClickUser : undefined}
            />
            {showUsersList && (
              <div ref={sentinelRef} className="shrink-0 min-h-px" />
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
